import React, { Component } from 'react';
import { connect } from 'react-redux';
import DragDrop from 'components/Draggable/DragDrop';
import { Tween, Timeline } from 'react-gsap';
import PropTypes from 'prop-types';
import InactivityManager from 'utils/InactivityManager';
import trashcanBottom from 'assets/images/trash-can-bottom.png';
import trashcanLid from 'assets/images/trash-can-lid.png';
import separator from 'assets/images/separator.svg';
import styles from './MatchItTrashIt.module.scss';
import { playMultipleSounds } from 'utils/play-multiple-sounds';
import { showVisualCue, hideVisualCue } from 'store/actions/ui';
import { t } from '../translations';

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const ERRORS = {
  ERROR_TYPES: {
    NotAMatch: 'NotAMatch',
    NotTrash: 'NotTrash',
    MissedTarget: 'MissedTarget'
  },
  COMPETENCY: {
    Matching: 'Matching',
    DragAndDrop: 'DragAndDrop'
  }
};

const REASON = {
  didNotMatchItem: 'Did not match item'
};

export class MatchItTrashIt extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bounce: false,
      done: false,
      round: 0,
      shake: false,
      trash: false,
      match: false,
      selectedImage: null,
      feedback: '',
      imageInfo: ''
    };

    this.soundPlaying = null;
    this.inactivityManager = new InactivityManager(
      TIMEOUT,
      () => this.playSound('beforeRound'),
      REASON.didNotMatchItem
    );
    this.errorData = [];
    this.promise = null;
    this.timers = [];
  }

  componentDidUpdate({ isVideoModalOpen }) {
    // video is being opened
    if (!isVideoModalOpen && this.props.isVideoModalOpen) {
      this.promise && this.promise.cancel();
      this.inactivityManager.clear();
      this.inactivityManager.stopAudio();
    }

    // video is being closed
    if (isVideoModalOpen && !this.props.isVideoModalOpen) {
      this.inactivityManager[this.initialized ? 'rewatch' : 'watch']();
      this.playActivityInstructions();
    }
  }

  addVisualCue(message, interval = 1000) {
    this.props.showVisualCue(message);
    this.setState({ feedback: message === 'success' ? t('GoodJob') : t('TryAgain') });
    this.timers.push(
      setTimeout(() => {
        this.props.hideVisualCue();
        this.setState({ feedback: ' ' });
      }, interval)
    );
  }

  playActivityInstructions = async () => {
    const {
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { beforeGame, beforeRound },
            rounds
          }
        }
      }
    } = this;

    if (this.initialized) return;
    this.initialized = true;
    this.promise = playMultipleSounds(500, audio, beforeGame, beforeRound, rounds[0].audio);
    return this.promise;
  };

  componentDidMount() {
    const { rounds } = this.props.currentRound.data;
    // we'll delay instructions until we close modal
    if (this.props.isVideoModalOpen) return;
    this.inactivityManager.watch();
    this.playActivityInstructions();
    this.feedbackRef = React.createRef();
    setTimeout(() => {
      this.setState({
        imageInfo: rounds[0].image.description + t('added')
      });
    }, 1000);
  }
  handleRoundEnd = () => {
    const {
      state: { round },
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { beforeRound },
            rounds
          },
          stopAllSounds
        }
      }
    } = this;

    stopAllSounds();

    if (round < 2) {
      this.setState({
        round: round + 1,
        trash: false,
        match: false,
        imageInfo: rounds[round + 1].image.description + t('added')
      });
      playMultipleSounds(0, audio, beforeRound, rounds[round + 1].audio);
      this.inactivityManager.watch();
      this.timers.push(setTimeout(() => this.setState({ imageInfo: ' ' }), 1000));
    } else {
      this.setState({ done: true });
      this.props.onRoundEnd();
      this.inactivityManager.clear();
      this.inactivityManager.stopAudio();
    }
  };

  onMatch = collision => {
    const { logError } = this;
    const { selectedImage } = this.state;

    this.inactivityManager.clear();
    this.inactivityManager.stopAudio();

    if (collision.isCorrect) {
      if (selectedImage) selectedImage.style['border'] = 'none';
      this.addVisualCue('success');

      const sound = collision.targetId === 'card' ? 'correctMatch' : 'correctTrash';
      if ('correctTrash' === sound) this.setState({ trash: true });
      else this.setState({ match: true });

      const snd = this.playSound(sound);
      const sndDuration = snd ? snd._duration * 1000 + 200 : 1000;

      this.timers.push(setTimeout(() => this.handleRoundEnd(), sndDuration));
      this.setState({ selectedImage: null });
    } else {
      this.addVisualCue('error');
      if (collision.targetId === 'card') {
        logError(ERRORS.ERROR_TYPES.NotAMatch);
        this.playSound('incorrectMatch');
      } else {
        logError(ERRORS.ERROR_TYPES.NotTrash);
        this.playSound('incorrectTrash');
      }
      if ('trashcan' === collision.targetId) this.setState({ shake: true });
      if ('card' === collision.targetId) this.setState({ bounce: true });
    }
  };

  onBadDrag = (x, y) => {
    this.logError(ERRORS.ERROR_TYPES.MissedTarget, { x, y });
  };

  logError = (errorType, { ...coords } = false) => {
    const {
      props: {
        logError,
        currentRound: {
          data: {
            rounds,
            target: {
              image: { id: targetId }
            }
          }
        }
      },
      state: { round }
    } = this;
    const timeStamp = new Date().toISOString();

    const error = {};
    switch (errorType) {
      case ERRORS.ERROR_TYPES.NotAMatch:
        error.error = ERRORS.ERROR_TYPES.NotAMatch;
        error.round = round + 1;
        error.competency = ERRORS.COMPETENCY.Matching;
        error.image = { id: rounds[round].image.id };
        error.expected = { trashed: true };
        error.actual = { id: targetId };
        error.timestamp = timeStamp;
        this.errorData.push(error);
        logError(error);
        break;

      case ERRORS.ERROR_TYPES.NotTrash:
        error.error = ERRORS.ERROR_TYPES.NotTrash;
        error.round = round + 1;
        error.competency = ERRORS.COMPETENCY.Matching;
        error.image = { id: rounds[round].image.id };
        error.expected = { id: targetId };
        error.actual = { trashed: true };
        error.timestamp = timeStamp;
        this.errorData.push(error);
        logError(error);
        break;

      case ERRORS.ERROR_TYPES.MissedTarget:
        error.error = ERRORS.ERROR_TYPES.MissedTarget;
        error.round = round + 1;
        error.competency = ERRORS.COMPETENCY.DragAndDrop;
        error.image = { id: rounds[round].image.id };
        error.expected = {
          ...(rounds[round].isMatch ? { id: targetId } : { trashed: true })
        };
        error.actual = { x: coords.x, y: coords.y };
        error.timestamp = timeStamp;
        this.errorData.push(error);
        logError(error);
        break;
    }
  };

  playSound = (...soundFiles) => {
    const {
      currentRound: {
        audio,
        data: { instructions }
      }
    } = this.props;
    const sounds = [];

    soundFiles.forEach((val, idx) => {
      const sound = audio[instructions[val].src];
      if (idx + 1 < soundFiles.length) {
        sound.on('end', () => sounds[idx + 1] && sounds[idx + 1].play());
      }
      sounds.push(sound);
    });

    if (sounds[0]) {
      if (this.soundPlaying) {
        this.soundPlaying.stop();
      }

      this.soundPlaying = sounds[0];
      this.soundPlaying.play();
      return this.soundPlaying;
    }
  };
  handleSelectImage = (ev, isSelected) => {
    this.setState({ selectedImage: isSelected ? ev.target : null });
  };

  handleSelectTarget = target => {
    const collision = {
      isCorrect: target.correct,
      targetId: target.id
    };

    this.onMatch(collision);
  };

  componentWillUnmount() {
    this.inactivityManager.clear();
    this.inactivityManager.stopAudio();
    this.props.currentRound.stopAllSounds();
    this.promise && this.promise.cancel();
    this.timers.forEach(id => clearTimeout(id));
  }

  render() {
    const {
      props: {
        currentRound: {
          data: {
            rounds,
            target: { image: targetImage }
          }
        },
        level,
        module
      },
      state: { bounce, round, shake, trash, match, selectedImage },
      onBadDrag,
      onMatch
    } = this;
    this.imageRef = React.createRef();

    return (
      <div className={styles.wrapper}>
        <div
          role="region"
          aria-live="assertive"
          ref={this.feedbackRef}
          className="feedback"
          style={{
            position: 'absolute',
            zIndex: '-1000',
            top: '50px',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
        >
          {this.state.feedback}
        </div>
        <div
          ref={this.imageRef}
          role="region"
          aria-live="polite"
          className="imageInfo"
          style={{
            position: 'absolute',
            zIndex: '-1000',
            top: '50px',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
        >
          {this.state.imageInfo}
        </div>
        {rounds.map((val, idx) => {
          if (idx === round) {
            const targets = [
              { ref: React.createRef(), correct: val.isMatch, id: 'card' },
              { ref: React.createRef(), correct: !val.isMatch, id: 'trashcan' }
            ];
            return (
              <div key={`round-${idx}`} className={styles.round}>
                <div className={styles.image} id="match-it-trash-it-card">
                  <DragDrop.DraggableWithKeyboard
                    activityType={'MatchItTrashIt'}
                    allowDrag={true}
                    allowTap={false}
                    animationTime={500}
                    callback={onMatch}
                    distance={10}
                    key={`match-it-${idx}`}
                    level={level}
                    mainContainerId={'activity-container'}
                    onBadDrag={onBadDrag}
                    resetTime={1000}
                    order={this.state.done ? -1 : 0}
                    scaling={true}
                    soundFx={{ sndWrong: false, sndCorrect: false, sndMoving: false }}
                    targets={targets}
                    onSelect={(ev, isSelected) => this.handleSelectImage(ev, isSelected)}
                  >
                    <div className={styles.imageWrapper} style={{ width: 192, height: 300 }}>
                      {/* the above container is necessary for computing limits */}
                      <img
                        className={trash || match ? styles.faded : ''}
                        src={val.image.src}
                        alt={val.image.description}
                      />
                    </div>
                  </DragDrop.DraggableWithKeyboard>
                </div>
                <div className={styles.separator}>
                  <img src={separator} alt={t('Separator')} />
                </div>
                <div className={styles.targets} id="match-it-trash-it-targets">
                  <DragDrop.Target
                    forwardedRef={targets[0].ref}
                    outline={selectedImage ? 'red' : null}
                    onDrop={() => selectedImage && this.handleSelectTarget(targets[0])}
                  >
                    <img
                      src={targetImage.src}
                      id="card-image"
                      data-correct={targets[0].correct}
                      className={module === 'letters' ? styles.large : ''}
                      alt={targetImage.description + t('matchPicture')}
                    />
                  </DragDrop.Target>
                  <DragDrop.Target
                    forwardedRef={targets[1].ref}
                    outline={selectedImage ? 'red' : null}
                    onDrop={() => selectedImage && this.handleSelectTarget(targets[1])}
                    ariaLabelDescription={t('trashCan')}
                  >
                    <div
                      className={styles.trashcanContainer}
                      id="trash-container"
                      data-correct={targets[1].correct}
                    >
                      {shake && (
                        <>
                          <Timeline
                            target={
                              <img className={styles.trashcanLid} src={trashcanLid} alt={''} />
                            }
                            onComplete={() => this.setState({ shake: false })}
                          >
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '+=5', rotation: '+=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '-=5', rotation: '-=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '+=5', rotation: '+=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '-=5', rotation: '-=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '+=5', rotation: '+=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '-=5', rotation: '-=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '+=5', rotation: '+=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ x: '-=5', rotation: '-=15' }}
                            />
                            <Tween ease={'Elastic.easeOut'} duration={1} to={{ y: '-=80' }} />
                            <Tween ease={'Power0.ease'} duration={0.5} to={{ y: '+=80' }} />
                          </Timeline>
                          <img className={styles.trashcanBottom} src={trashcanBottom} alt={''} />
                        </>
                      )}
                      {bounce && (
                        <>
                          <Timeline
                            repeat={3}
                            target={
                              <img className={styles.trashcanLid} src={trashcanLid} alt={''} />
                            }
                            onComplete={() => this.setState({ bounce: false })}
                          >
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ y: '-=35', rotation: '-=15' }}
                            />
                            <Tween
                              duration={0.08}
                              yoyo={true}
                              to={{ y: '+=35', rotation: '+=15' }}
                            />
                          </Timeline>
                          <img className={styles.trashcanBottom} src={trashcanBottom} alt={''} />
                        </>
                      )}
                      {trash && (
                        <>
                          <Timeline
                            repeat={1}
                            target={
                              <img className={styles.trashcanLid} src={trashcanLid} alt={''} />
                            }
                          >
                            <Tween
                              ease={'Elastic.ease'}
                              duration={0.2}
                              to={{ x: '-=20', y: '-=40', rotation: '-=40' }}
                            />
                            <Tween
                              duration={0.2}
                              ease={'Power0.ease'}
                              to={{ x: '+=25', y: '+=40', rotation: '+=50' }}
                            />
                          </Timeline>
                          <img className={styles.trashcanBottom} src={trashcanBottom} alt={''} />
                        </>
                      )}
                      {!shake && !bounce && !trash && (
                        <>
                          <img className={styles.trashcanLid} src={trashcanLid} alt={''} />
                          <img className={styles.trashcanBottom} src={trashcanBottom} alt={''} />
                        </>
                      )}
                    </div>
                  </DragDrop.Target>
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  }
}

MatchItTrashIt.propTypes = {
  commonSounds: PropTypes.object,
  currentRound: PropTypes.shape({
    type: PropTypes.string,
    images: PropTypes.array,
    audio: PropTypes.array,
    data: PropTypes.shape({
      instructions: PropTypes.object,
      rounds: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          image: PropTypes.object,
          audio: PropTypes.object,
          isMatch: PropTypes.bool
        })
      ),
      target: PropTypes.object
    }),
    stopAllSounds: PropTypes.func
  }),
  level: PropTypes.number,
  logError: PropTypes.func,
  module: PropTypes.string,
  onRoundEnd: PropTypes.func,
  isVideoModalOpen: PropTypes.bool,
  showVisualCue: PropTypes.func,
  hideVisualCue: PropTypes.func
};

const mapStateToProps = state => ({
  isVideoModalOpen: state.activity.isVideoModalOpen,
  module: state.ui.module
});
const mapDispatchToProps = {
  showVisualCue,
  hideVisualCue
};

export default connect(mapStateToProps, mapDispatchToProps)(MatchItTrashIt);
